import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useContext } from 'react';
import SessionContext from '../../contexts/SessionContext';
import API_URL from '../../constants/environment';
import { ProductivityStatsResponse, ReportingComparison } from '../../types/reporting';

interface ProductivityStatsRequest {
    startDate: string;
    endDate: string;
    locationId?: string;
    sortBy?: string;
    sortOrder?: 'asc' | 'desc';
    preset?: string;
    comparison?: ReportingComparison;
    includeComparison?: boolean;
    excludeEvs?: boolean;
}

const getProductivityStats = async (
    sessionId: string,
    input: ProductivityStatsRequest
): Promise<ProductivityStatsResponse> => {
    const { data } = await axios.post<ProductivityStatsResponse>(
        `${API_URL}/reporting/productivity/stats`,
        input,
        { headers: { Authorization: sessionId } }
    );
    return data;
};

export const useProductivityStats = (
    startDate: string,
    endDate: string,
    options?: {
        locationId?: string;
        preset?: string;
        comparison?: ReportingComparison;
        excludeEvs?: boolean;
        enabled?: boolean;
    }
) => {
    const sessionContext = useContext(SessionContext);
    const { comparison, excludeEvs, enabled = true, ...restOptions } = options || {};

    return useQuery({
        queryKey: ['reporting', 'productivity', startDate, endDate, restOptions.locationId, restOptions.preset, comparison, excludeEvs],
        queryFn: () => getProductivityStats(sessionContext?.sessionId ?? '', {
            startDate,
            endDate,
            ...restOptions,
            comparison,
            includeComparison: !!comparison,
            excludeEvs,
        }),
        enabled: enabled && !!sessionContext?.sessionId && !!startDate && !!endDate,
        staleTime: 1000 * 60 * 5,
    });
};
